import { useState } from 'react';
import { WORKSPACE_ONE_TOKEN_URL_BY_REGION } from '@kw/constants';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { QCardContent, QTypography, QButton, QTextField, QDropdownWrapper } from '@kw/quokka-ui';
import { styled } from '@mui/material';
import { SettingsMode } from './enum/SettingsMode.enum';
import { sendHttpRequest } from '../../utils/network.service';
import './integration-card.css';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { KwFormErrorMessage } from '../../kw-ui-components/KwFormErrorMessage';
import { MDM_INTEGRATION_SETTINGS_ID } from '../shared/constants';
import { StyledIntegrationCard } from '../shared/StyledIntegrationCard';
import { MdmIntegrationCardPauseButton, MdmIntegrationCardStatus } from '../shared/MdmIntegrationStatusControl';
import { isPaused, MdmIntegrationStatus } from '../shared/mdmIntegrationStatus';
import { MDM_SUBDOMAIN_LABEL_PATTERN } from '../../utils/mdmSubdomain';

interface WorkspaceOneIntegrationCardProps {
  subDomainPlaceholder: string;
  regionPlaceholder: string;
  clientIdPlaceholder: string;
  clientSecretPlaceholder: string;
  lastSync: string;
  isAdmin: boolean;
  mode: { [key: string]: SettingsMode };
  setMode: React.Dispatch<React.SetStateAction<{ [key: string]: SettingsMode }>>;
  mutate: () => void;
  /** Undefined until the integration is configured. */
  status?: MdmIntegrationStatus;
  onStatusChange: () => void;
}

const regionAuth0TokenUrlOptions = [
  {
    value: WORKSPACE_ONE_TOKEN_URL_BY_REGION.na,
    label: 'North America (US & Canada data centers)',
  },
  {
    value: WORKSPACE_ONE_TOKEN_URL_BY_REGION.emea,
    label: 'EMEA (UK & Germany data centers)',
  },
  {
    value: WORKSPACE_ONE_TOKEN_URL_BY_REGION.apac,
    label: 'APAC (India, Japan, Singapore, Australia & Hong Kong data centers)',
  },
  {
    value: WORKSPACE_ONE_TOKEN_URL_BY_REGION.uat,
    label: 'UAT environment',
  },
];

export const WorkspaceOneIntegrationCard = ({
  subDomainPlaceholder,
  regionPlaceholder,
  clientIdPlaceholder,
  clientSecretPlaceholder,
  lastSync,
  isAdmin,
  mode,
  setMode,
  mutate,
  status,
  onStatusChange,
}: WorkspaceOneIntegrationCardProps) => {
  const isEditMode = mode.workspaceOne === SettingsMode.EDIT;
  const isReadMode = mode.workspaceOne === SettingsMode.READ;
  const [subDomain, setSubDomain] = useState('');
  const [region, setRegion] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [WorkspaceOneFormErrors, setWorkspaceOneFormErrors] = useState({
    subDomain: false,
    region: false,
    clientId: false,
    clientSecret: false,
    validated: false,
  });
  const [connectionFailed, setConnectionFailed] = useState(false);
  const lastSyncTimestamp = lastSync ? new Date(parseInt(lastSync, 10)).toLocaleString() : 'Never';

  const resetInputValues = () => {
    setSubDomain('');
    setRegion('');
    setClientId('');
    setClientSecret('');
    setConnectionFailed(false);
    setWorkspaceOneFormErrors({ subDomain: false, region: false, clientId: false, clientSecret: false, validated: false });
    setMode({ ...mode, workspaceOne: SettingsMode.READ });
  };

  function validateWorkspaceOneSettings() {
    const validSubdomain = MDM_SUBDOMAIN_LABEL_PATTERN.test(subDomain);
    setWorkspaceOneFormErrors({
      subDomain: !validSubdomain,
      region: !region.length,
      clientId: !clientId.length,
      clientSecret: !clientSecret.length,
      validated: true,
    });
    return validSubdomain;
  }

  const syncNow = async () => {
    await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/mdm-integrations/${MDM_INTEGRATION_SETTINGS_ID.WORKSPACE_ONE}/sync`,
      method: 'POST',
      body: {},
      successMessage: 'Workspace One sync initiated successfully',
      errorMessage: 'Error initiating Workspace One sync',
      enqueueSnackbar,
    });
  };

  const updateWorkspaceOneSettings = async () => {
    const validSubdomain = validateWorkspaceOneSettings();

    if (!clientId.length || !clientSecret.length || !validSubdomain || !region.length) {
      return;
    }

    const existingSettings = clientId.length || clientSecret.length || subDomain.length || region.length;
    let path = `${process.env.ORGANIZATION_SERVICE_URL}`;
    path += existingSettings
      ? `/organizations/mdm-integrations/${MDM_INTEGRATION_SETTINGS_ID.WORKSPACE_ONE}`
      : '/organizations/mdm-integrations';
    const method = existingSettings ? 'PUT' : 'POST';

    const body = existingSettings
      ? {}
      : {
          mdmIntegrationId: MDM_INTEGRATION_SETTINGS_ID.WORKSPACE_ONE,
          settings: {
            client_id: clientId,
            client_secret: clientSecret,
            subdomain: subDomain,
            region: regionAuth0TokenUrlOptions.find(option => option.value === region)?.label,
            token_url: regionAuth0TokenUrlOptions.find(option => option.value === region)?.value || '',
          },
        };
    if (existingSettings) {
      const settings = {};
      if (clientId.length) {
        Object.assign(settings, { ...settings, client_id: clientId });
      }
      if (clientSecret.length) {
        Object.assign(settings, { ...settings, client_secret: clientSecret });
      }
      if (subDomain.length) {
        Object.assign(settings, { ...settings, subdomain: subDomain });
      }
      if (region.length) {
        Object.assign(settings, {
          ...settings,
          region: regionAuth0TokenUrlOptions.find(option => option.value === region)?.label,
          token_url: regionAuth0TokenUrlOptions.find(option => option.value === region)?.value || '',
        });
      }

      Object.assign(body, { settings });
    }

    const result = await sendHttpRequest({
      path,
      method,
      body,
      successMessage: 'Workspace One settings updated successfully',
      errorMessage: 'Error updating Workspace One settings',
      enqueueSnackbar,
    });

    if (result.error) return;

    // A paused integration refuses to sync, and saving credentials doesn't resume it.
    if (!isPaused(status)) await syncNow();

    mutate();
    // A first save adds the integration to the status list, which shows its Pause button.
    onStatusChange();
    resetInputValues();
  };

  const flags: LaunchDarklyFlags['flags'] = useFlags();

  if (!flags?.workspaceOneIntegration || !verifyAuthPermission(AuthPermissions.SETTINGS_INTEGRATIONS_MDM)) return null;

  return (
    <StyledIntegrationCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold" gutterBottom>
          <img src="/logo/workspace-one.png" className="logo" />
          Omnissa Workspace ONE UEM
        </StyledQTypography>
        <KwFormErrorMessage error={Boolean(connectionFailed)} reason="We couldn't make a connection with the information provided" />
        <StyledQTypography variant="bodyRegular" gutterBottom>
          Configure Q-scout’s connection to the Omnissa Workspace ONE API to import Mobile Device and App Information from your Workspace
          ONE UEM tenant.{' '}
          <a
            href="https://docs.omnissa.com/bundle/SystemSettingsV2410/page/RestAPIforWorkspaceONEUEM.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </StyledQTypography>
        {isAdmin && isEditMode ? (
          <>
            <StyledQTypography variant="h5Bold">Region</StyledQTypography>
            <QDropdownWrapper
              selectedOption={region}
              closedLabel={regionAuth0TokenUrlOptions.find(option => option.value === region)?.label ?? 'Select Region'}
              formControlProps={{ sx: { width: '100%' } }}
              onChange={e => setRegion(e.target.value as string)}
              options={regionAuth0TokenUrlOptions}
            />
            <KwFormErrorMessage error={Boolean(WorkspaceOneFormErrors.region)} reason="Select the Workspace One region" />

            <StyledQTypography variant="h5Bold">SubDomain</StyledQTypography>
            <QTextField
              placeholder={subDomainPlaceholder}
              onChange={e => setSubDomain(e.target.value)}
              error={Boolean(WorkspaceOneFormErrors.subDomain)}
            />
            <KwFormErrorMessage
              error={Boolean(WorkspaceOneFormErrors.subDomain)}
              reason="Enter a Workspace ONE subdomain using only letters, numbers, and hyphens"
            />

            <StyledQTypography variant="h5Bold">Client ID</StyledQTypography>
            <QTextField
              placeholder={clientIdPlaceholder}
              onChange={e => setClientId(e.target.value)}
              error={Boolean(WorkspaceOneFormErrors.clientId)}
            />
            <KwFormErrorMessage error={Boolean(WorkspaceOneFormErrors.clientId)} reason="Enter the Workspace One Client ID" />

            <StyledQTypography variant="h5Bold">Client Secret</StyledQTypography>
            <QTextField
              placeholder={clientSecretPlaceholder}
              onChange={e => setClientSecret(e.target.value)}
              error={Boolean(WorkspaceOneFormErrors.clientSecret)}
            />
            <KwFormErrorMessage error={Boolean(WorkspaceOneFormErrors.clientSecret)} reason="Enter the Workspace One Client Secret" />
          </>
        ) : (
          <>
            <StyledQTypography variant="h5Bold">Region</StyledQTypography>
            <StyledQTypography>{regionPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">SubDomain</StyledQTypography>
            <StyledQTypography>{subDomainPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Client ID</StyledQTypography>
            <StyledQTypography>{clientIdPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Client Secret</StyledQTypography>
            <StyledQTypography>{clientSecretPlaceholder}</StyledQTypography>
          </>
        )}

        <MdmIntegrationCardStatus status={status} />

        <div className="action-wrapper">
          {isAdmin && isEditMode ? (
            <>
              <QButton onClick={resetInputValues}>Cancel</QButton>
              <QButton variant="filled" onClick={updateWorkspaceOneSettings}>
                Save
              </QButton>
            </>
          ) : (
            <>
              {isAdmin && isReadMode ? (
                <>
                  <div className="sync-action-wrapper">
                    <QButton
                      style={{ padding: 0 }}
                      variant="text"
                      onClick={() => syncNow()}
                      disabled={
                        !clientIdPlaceholder || !clientSecretPlaceholder || !subDomainPlaceholder || !regionPlaceholder || isPaused(status)
                      }
                    >
                      <img src="icons/sync-now.svg" className="sync-now-icon" />
                      <span className="sync-now-button-text">Sync Now</span>
                    </QButton>
                    <QTypography sx={{ color: '#757575' }}>Last synced: {lastSyncTimestamp}</QTypography>
                  </div>
                  <MdmIntegrationCardPauseButton label="Workspace One" status={status} onChange={onStatusChange} />
                  <QButton variant="filled" onClick={() => setMode({ ...mode, workspaceOne: SettingsMode.EDIT })}>
                    Edit
                  </QButton>
                </>
              ) : (
                <QButton disabled variant="filled">
                  Save
                </QButton>
              )}
            </>
          )}
        </div>
      </QCardContent>
    </StyledIntegrationCard>
  );
};

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;
